import type { AnchorSide } from '@/types'
import type { CardinalityKind } from '@/utils/relationships'

interface CardinalityMarkProps {
  x: number
  y: number
  side: AnchorSide
  type: CardinalityKind
  color: string
}

function offsetOrigin(x: number, y: number, side: AnchorSide, distance = 14) {
  switch (side) {
    case 'left':
      return { x: x - distance, y }
    case 'right':
      return { x: x + distance, y }
    case 'top':
      return { x, y: y - distance }
    case 'bottom':
      return { x, y: y + distance }
  }
}

function getAxes(side: AnchorSide) {
  switch (side) {
    case 'left':
      return { aX: 1, aY: 0, pX: 0, pY: 1 }
    case 'right':
      return { aX: -1, aY: 0, pX: 0, pY: 1 }
    case 'top':
      return { aX: 0, aY: 1, pX: 1, pY: 0 }
    case 'bottom':
      return { aX: 0, aY: -1, pX: 1, pY: 0 }
  }
}

function projectPoint(
  originX: number,
  originY: number,
  aX: number,
  aY: number,
  pX: number,
  pY: number,
  along: number,
  perp: number,
) {
  return {
    x: originX + along * aX + perp * pX,
    y: originY + along * aY + perp * pY,
  }
}

function MarkLine(props: {
  x: number
  y: number
  aX: number
  aY: number
  pX: number
  pY: number
  fromAlong: number
  fromPerp: number
  toAlong: number
  toPerp: number
  color: string
}) {
  const start = projectPoint(
    props.x,
    props.y,
    props.aX,
    props.aY,
    props.pX,
    props.pY,
    props.fromAlong,
    props.fromPerp,
  )
  const end = projectPoint(
    props.x,
    props.y,
    props.aX,
    props.aY,
    props.pX,
    props.pY,
    props.toAlong,
    props.toPerp,
  )

  return (
    <line
      x1={start.x}
      y1={start.y}
      x2={end.x}
      y2={end.y}
      stroke={props.color}
      strokeWidth={1.5}
      strokeLinecap="round"
    />
  )
}

export function CardinalityMark({
  x,
  y,
  side,
  type,
  color,
}: CardinalityMarkProps) {
  const { aX, aY, pX, pY } = getAxes(side)
  // Crow's-foot arms reach 19–20px toward the table. Keep their tips on the
  // relationship line, just outside the table boundary, instead of over it.
  const origin = offsetOrigin(x, y, side, type === 'many' || type === 'zero-many' ? 20 : 14)

  return (
    <g pointerEvents="none" aria-hidden="true">
      {type === 'one' && (
        <>
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={0} fromPerp={-6} toAlong={0} toPerp={6} color={color} />
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={6} fromPerp={-6} toAlong={6} toPerp={6} color={color} />
        </>
      )}

      {type === 'many' && (
        <>
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={0} fromPerp={-6} toAlong={0} toPerp={6} color={color} />
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={7} fromPerp={0} toAlong={19} toPerp={0} color={color} />
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={7} fromPerp={0} toAlong={19} toPerp={-7} color={color} />
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={7} fromPerp={0} toAlong={19} toPerp={7} color={color} />
        </>
      )}

      {type === 'zero-one' && (
        <>
          <circle cx={origin.x} cy={origin.y} r={4.5} fill="none" stroke={color} strokeWidth={1.5} />
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={9} fromPerp={-6} toAlong={9} toPerp={6} color={color} />
        </>
      )}

      {type === 'zero-many' && (
        <>
          <circle cx={origin.x} cy={origin.y} r={4.5} fill="none" stroke={color} strokeWidth={1.5} />
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={8} fromPerp={0} toAlong={20} toPerp={0} color={color} />
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={8} fromPerp={0} toAlong={20} toPerp={-7} color={color} />
          <MarkLine x={origin.x} y={origin.y} aX={aX} aY={aY} pX={pX} pY={pY} fromAlong={8} fromPerp={0} toAlong={20} toPerp={7} color={color} />
        </>
      )}
    </g>
  )
}
